import * as XLSX from "xlsx";
import { getOfficialUnitName } from "../../purchasing/utils/unitNames";

export const CATALOG_IMPORT_MAX_ROWS = 5000;
export const CATALOG_IMPORT_MAX_BYTES = 5 * 1024 * 1024;
export const CATALOG_IMPORT_TYPES = {
  item: { label: "items", sheet: "Items" },
  supplier: { label: "suppliers", sheet: "Suppliers" },
  family: { label: "families", sheet: "Families" },
  subfamily: { label: "subfamilies", sheet: "Subfamilies", parentKind: "family", parentField: "family_code", parentLabel: "Family" },
  category: { label: "categories", sheet: "Categories", parentKind: "subfamily", parentField: "subfamily_code", parentLabel: "Subfamily" },
  subcategory: { label: "subcategories", sheet: "Subcategories", parentKind: "category", parentField: "category_code", parentLabel: "Category" },
};
export const CATALOG_IMPORT_COLUMNS = {
  item: [
    ["code", "Item code", "Required. New internal code, up to 50 characters. Keep codes as text to preserve leading zeros."],
    ["name", "Item name", "Required. Up to 255 characters."],
    ["category_code", "Category code", "Required. Existing active code from Categories. Family and subfamily follow this category."],
    ["subcategory_code", "Subcategory code", "Optional. Existing active code from Subcategories, belonging to the chosen category."],
    ["unit_code", "Unit code", "Required. Existing active purchase unit from Units."],
    ["supplier_codes", "Supplier codes", "Required. Existing active stock supplier codes from Suppliers, separated by semicolons. Import suppliers first if needed."],
    ["supplier_item_code", "Supplier item code", "Optional supplier reference, distinct from the internal item code. Up to 100 characters."],
    ["description", "Description", "Optional. Up to 5,000 characters."],
    ["subunit_code", "Subunit code", "Optional first subunit code from Units. Requires Units per pack."],
    ["pack_size", "Units per pack", "Positive number of first subunits per purchase unit. Requires Subunit code."],
    ["second_subunit_code", "Second subunit code", "Optional second subunit code from Units. Requires first subunit and Subunits per subunit."],
    ["subunit2_size", "Subunits per subunit", "Positive number of second subunits per first subunit."],
    ["minimum_purchase_quantity", "Minimum purchase", "Optional positive quantity in purchase units, up to 4 decimal places."],
    ["purchase_multiple", "Purchase multiple", "Optional positive increment in purchase units, up to 4 decimal places. Independent of pack size."],
    ["subunit_justification", "First subunit justification", "For a combination requiring review, explain why the first subunit and its quantity are correct (up to 1,000 characters)."],
    ["subunit2_justification", "Second subunit justification", "For a combination requiring review, explain why the second subunit and its quantity are correct (up to 1,000 characters)."],
  ],
  supplier: [
    ["code", "Supplier code", "Required. New corporate code, up to 100 characters. Keep codes as text."],
    ["name", "Supplier name", "Required. Up to 255 characters."],
    ["supplier_id", "External supplier code", "Optional external supplier reference, up to 100 characters. Blank uses the corporate code when published."],
    ["tax_id", "Tax ID", "Optional tax identifier, up to 100 characters."],
    ["contact_name", "Contact name", "Optional contact person, up to 255 characters."],
    ["email", "Email", "Optional email addresses, separated by semicolons."],
    ["phone", "Phone", "Optional. Up to 100 characters. Keep as text to preserve leading zeros and country prefixes."],
    ["address", "Address", "Optional. Up to 500 characters."],
    ["communication_language", "Communication language", "en, es, ca or fr. Blank defaults to en."],
    ["is_stock_supplier", "Stock supplier", "Yes or No. Blank defaults to Yes."],
    ["is_service_supplier", "Service supplier", "Yes or No. Blank defaults to No. Both stock and service may be Yes."],
    ["minimum_order_amount", "Minimum order amount", "Minimum net amount per purchase order, in the order currency. Leave blank or enter 0 for no minimum."],
    ["description", "Description", "Optional. Up to 5,000 characters."],
  ],
  family: [
    ["code", "Family code", "Required. New corporate code, up to 50 characters. Keep codes as text to preserve leading zeros."],
    ["name", "Family name", "Required. Up to 120 characters."],
    ["description", "Description", "Optional. Up to 5,000 characters."],
  ],
  subfamily: [
    ["code", "Subfamily code", "Required. New corporate code, up to 50 characters. Keep codes as text to preserve leading zeros."],
    ["name", "Subfamily name", "Required. Up to 120 characters."],
    ["family_code", "Family code", "Required. Existing active family code from Families. Import missing families first."],
    ["description", "Description", "Optional. Up to 5,000 characters."],
  ],
  category: [
    ["code", "Category code", "Required. New corporate code, up to 50 characters. Keep codes as text to preserve leading zeros."],
    ["name", "Category name", "Required. Up to 120 characters."],
    ["subfamily_code", "Subfamily code", "Required. Existing active subfamily code from Subfamilies. Import missing subfamilies first."],
    ["description", "Description", "Optional. Up to 5,000 characters."],
  ],
  subcategory: [
    ["code", "Subcategory code", "Required. New corporate code, up to 50 characters. Keep codes as text to preserve leading zeros."],
    ["name", "Subcategory name", "Required. Up to 120 characters."],
    ["category_code", "Category code", "Required. Existing active category code from Categories. Import missing categories first."],
    ["description", "Description", "Optional. Up to 5,000 characters."],
  ],
};
const sheetName = (kind) => CATALOG_IMPORT_TYPES[kind].sheet;
function addSheet(workbook, name, rows, widths) {
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  sheet["!cols"] = (widths || rows[0].map(() => 28)).map((wch) => ({ wch }));
  if (rows.length > 1) sheet["!autofilter"] = { ref: sheet["!ref"] };
  XLSX.utils.book_append_sheet(workbook, sheet, name);
  return sheet;
}

export function createCatalogTemplate(kind, catalog = [], measurementUnits = [], languageId = "en") {
  const columns = CATALOG_IMPORT_COLUMNS[kind];
  if (!Array.isArray(columns)) throw new Error("Choose a valid catalogue type.");
  const type = CATALOG_IMPORT_TYPES[kind];
  const workbook = XLSX.utils.book_new();
  workbook.Props = { Title: `Pilcore corporate ${sheetName(kind).toLowerCase()} import`, Author: "Pilcore" };
  const sheet = addSheet(workbook, sheetName(kind), [columns.map((column) => column[1])]);
  for (let row = 1; row <= 100; row += 1) columns.forEach((_, col) => {
    sheet[XLSX.utils.encode_cell({ r: row, c: col })] = { t: "s", v: "", z: "@" };
  });
  sheet["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: 100, c: columns.length - 1 } });
  addSheet(workbook, "Instructions", [
    ["CORPORATE CATALOGUE IMPORT", "Instructions"],
    ["Fill the template", `One new record per row in ${sheetName(kind)}. Maximum 5,000 rows and 5 MB per file. Keep the sheet name and all column headings.`],
    ["New records only", "Existing codes, including inactive records, are rejected. The import never overwrites catalogue records. Codes are compared without case or surrounding spaces."],
    ["Review and save", "Upload the file, review the validation results and confirm the import. If any row is invalid, nothing is saved. Download the error list, correct the workbook and upload again."],
    ["Formatting", "Use plain values, no formulas or merged cells. Keep codes and phone numbers as text. Numeric quantities accept a decimal point or comma without thousands separators."],
    ["Scope", "Records are created in the shared corporate catalogue. This import does not publish them to hotels or create tenders."],
    ["Reference sheets", "Reference sheets are for lookup only and are not imported. Fill only the input sheet for the selected catalogue type."],
    ...(kind !== "supplier" ? [["Import order", "Import families, then subfamilies, categories and subcategories from their respective corporate catalogue tabs. Each parent must already exist and be active before importing its children. Import suppliers before items."]] : []),
    ...(type.parentKind ? [["Parent codes", `Use an existing active ${type.parentLabel.toLowerCase()} code from the ${sheetName(type.parentKind)} reference sheet. Import missing parents in their own catalogue tab first, then download a fresh template.`]] : []),
    ...(kind === "item" ? [["Item references", "Use existing categories, subcategories, units and stock suppliers from the reference sheets. Import missing classifications with Excel in their respective corporate catalogue tabs; import missing suppliers from Corporate suppliers."],
      ["Example", "Item code: COF-NEW | Item name: Coffee beans | Category code: a code from Categories | Unit code: a code from Units | Supplier codes: SUP-A;SUP-B"]] : []),
    [], ["Column", "How to fill it in"], ...columns.map((column) => [column[1], column[2]]),
  ], [30, 110]);
  const active = catalog.filter((entry) => entry.is_active !== false), byId = new Map(catalog.map((entry) => [Number(entry.id), entry]));
  if (type.parentKind) {
    addSheet(workbook, sheetName(type.parentKind), [[`${type.parentLabel} code`, "Name"], ...active.filter((entry) => entry.kind === type.parentKind).map((entry) => [entry.code, entry.name])]);
  }
  if (kind === "item") {
    addSheet(workbook, "Categories", [["Category code", "Name", "Subfamily", "Family"], ...active.filter((entry) => entry.kind === "category").map((entry) => {
      const parent = byId.get(Number(entry.parent_id));
      return [entry.code, entry.name, parent?.name || "", byId.get(Number(parent?.parent_id))?.name || ""];
    })]);
    addSheet(workbook, "Subcategories", [["Subcategory code", "Name", "Category code"], ...active.filter((entry) => entry.kind === "subcategory").map((entry) => [entry.code, entry.name, byId.get(Number(entry.parent_id))?.code || ""])]);
    const corporateUnits = active.filter((entry) => entry.kind === "unit" && !entry.data?.purchasing_unit_id && !entry.is_hidden);
    const units = corporateUnits.map(entry => [entry.code, getOfficialUnitName(entry, languageId), entry.data?.type || "", "Corporate"]);
    for (const unit of measurementUnits.filter((entry) => entry.source === "purchasing" && entry.business_unit_id == null && entry.is_active !== false && !entry.is_hidden)) {
      if (!corporateUnits.some(entry => String(entry.code).toLowerCase() === String(unit.code).toLowerCase() && String(entry.name).toLowerCase() === String(unit.name).toLowerCase() && entry.data?.type === unit.type)) {
        units.push([unit.code, getOfficialUnitName(unit, languageId), unit.type, "Common"]);
      }
    }
    addSheet(workbook, "Units", [["Unit code", "Name", "Type", "Scope"], ...units]);
    addSheet(workbook, "Suppliers", [["Supplier code", "Name"], ...active.filter((entry) => entry.kind === "supplier" && entry.data?.is_stock_supplier !== false).map((entry) => [entry.code, entry.name])]);
  }
  addSheet(workbook, "Existing codes", [["Code", "Name", "Status"], ...catalog.filter((entry) => entry.kind === kind).map((entry) => [entry.code, entry.name, entry.is_active === false ? "Inactive" : "Active"])]);
  return workbook;
}

export function downloadCatalogTemplate(kind, catalog, units, languageId) {
  XLSX.writeFile(createCatalogTemplate(kind, catalog, units, languageId), `pilcore-corporate-${sheetName(kind).toLowerCase()}-template.xlsx`);
}

export function parseCatalogWorkbook(data, kind) {
  const definitions = CATALOG_IMPORT_COLUMNS[kind];
  if (!Array.isArray(definitions)) throw new Error("Choose a valid catalogue type.");
  const name = sheetName(kind);
  const workbook = XLSX.read(data, { type: "array", cellFormula: true, sheetRows: CATALOG_IMPORT_MAX_ROWS + 2, sheets: [name] });
  const source = workbook.Sheets[name];
  if (!source?.["!ref"]) throw new Error(`Use the downloaded template with its "${name}" sheet.`);
  if (source["!merges"]?.length) throw new Error(`Remove merged cells from the ${name} sheet.`);
  const range = XLSX.utils.decode_range(source["!fullref"] || source["!ref"]);
  if (range.e.r > CATALOG_IMPORT_MAX_ROWS || range.e.c > 100) throw new Error("The sheet exceeds the template limits. Keep at most 5,000 rows and remove unused columns.");
  const columns = new Map();
  definitions.forEach(([field, label]) => {
    let index = -1;
    for (let col = 0; col <= range.e.c; col += 1) {
      const cell = source[XLSX.utils.encode_cell({ r: 0, c: col })];
      if (cell?.f || cell?.t === "e") throw new Error("Remove formulas and errors from the header.");
      if (String(cell?.v || "").trim() === label) {
        if (index !== -1) throw new Error(`Duplicate column: ${label}.`);
        index = col;
      }
    }
    if (index === -1 && ["subunit_justification", "subunit2_justification", "minimum_order_amount"].includes(field)) return;
    if (index === -1) throw new Error(`Missing column: ${label}. Download a fresh template.`);
    columns.set(field, index);
  });
  const rows = [], errors = [];
  for (let row = 1; row <= range.e.r; row += 1) {
    const entry = { row: row + 1 };
    let populated = false;
    for (const [field, col] of columns) {
      const cell = source[XLSX.utils.encode_cell({ r: row, c: col })];
      if (cell?.f || cell?.t === "e") { errors.push({ row: row + 1, message: cell.f ? "Replace formulas with plain values." : "Correct the Excel error in this row." }); populated = true; continue; }
      entry[field] = cell?.v ?? "";
      if (typeof cell?.v === "number" && (field.includes("code") || ["phone", "tax_id", "supplier_id"].includes(field))) entry[field] = cell.w || String(cell.v);
      if (String(entry[field]).trim()) populated = true;
    }
    if (populated) rows.push(entry);
  }
  if (!rows.length) throw new Error(`The ${name} sheet is empty. Fill at least one row.`);
  return { rows, errors };
}

export function downloadCatalogImportErrors(errors) {
  const workbook = XLSX.utils.book_new();
  addSheet(workbook, "Errors", [["Excel row", "Error"], ...errors.map((entry) => [entry.row, entry.message])], [16, 110]);
  XLSX.writeFile(workbook, "pilcore-catalogue-import-errors.xlsx");
}
